seo:
  ogImageAlt: Tale logo
  home:
    title: "Tale: Project Workspace for Teams and AI Agents"
    description: Plan projects, delegate tasks to AI agents, and review results with your
      team. An open-source workspace with agent sandboxes, shared context, and
      workflows.
  pricing:
    title: How much does Tale cost? — Community & Enterprise
    description:
      Tale Enterprise is CHF 12 (EUR 14) per user/month, two months free
      on yearly billing. Community is free to self-host as open-source software.
  contact:
    title: Contact the Tale team — sales, support, demos
    description:
      "Talk to the Tale team about coordinating teammates and AI agents:
      project workflows, deployment, pricing, or a guided demo."
  about:
    title: Who is behind Tale? — About Ruler GmbH
    description: Tale is built by Ruler GmbH in Spiez, Switzerland. We build an
      open-source project workspace where teams coordinate AI agents and
      review their work.
  requestDemo:
    title: Request a Tale demo — a guided walkthrough
    description:
      Bring a task your team needs to solve. See how Tale coordinates
      teammates and AI agents through project boards, sandbox work, and
      review.
  hardwarePricing:
    title: What does Tale AI hardware cost?
    description:
      Silent inference hardware from single nodes to racks — rent, lease,
      or buy. Deploy on-site with remote maintenance, or host in secure data
      centers.
  platform:
    title: AI Agent Coordination and Project Management | Tale
    description: Bring people, AI agents, tasks, and project files into one workspace.
      See how Tale connects task boards, agent sandboxes, knowledge, and
      repeatable workflows.
  platformAgents:
    title: AI Agents for Project Tasks | Tale
    description:
      Give project agents instructions, tools, and a runtime such as Claude
      Code or Codex. Assign tasks, inspect sandbox files, and review what the
      agents deliver.
  platformChat:
    title: AI chat with sources and task handoff | Tale
    description: Ask questions, inspect sources, compare two models in Arena, and turn a
      conversation into a project task for an AI agent in Tale.
  platformAutomations:
    title: Repeatable AI Agent Workflows | Tale
    description: Turn recurring work into workflows with agent steps, conditions,
      connectors, and configured approvals. Start runs manually or with
      schedules and webhooks.
  platformKnowledge:
    title: Shared Knowledge for AI Project Work | Tale
    description:
      Give AI work the context it needs. Index documents, websites, and
      knowledge entries, retrieve accessible sources, and check citations in
      the same workspace.
  platformGovernance:
    title: AI governance, approvals and usage controls | Tale
    description:
      Configure connector approvals, review recorded actions, set model usage
      budgets, and apply chat guardrails for your team and AI agents in Tale.
  platformProjects:
    title: Shared Task Boards for Teams and AI Agents | Tale
    description:
      Plan work with teammates and AI agents on a shared project board. Keep
      tasks, files, conversations, and reviews together from the first request
      to the result.
  changelog:
    title: What's new in Tale? — Changelog
    description: Release notes for every Tale version, newest first — the
      features, fixes, and breaking changes in each one, published from GitHub
      Releases.
notFound:
  title: This page doesn't exist
  body: The address may be mistyped, or the page has moved. The homepage is the
    best place to start over.
  backHome: Back to the homepage
nav:
  platform: Platform
  resources: Resources
  pricing: Pricing
  openMenu: Open menu
  closeMenu: Close menu
  homeAriaLabel: Tale home
  skipToMain: Skip to main content
  product:
    hub:
      label: Platform overview
      description: How people, agents, and project work fit together.
    agents:
      label: Agents
      description: Delegate tasks to agents with their own tools and runtime.
    chat:
      label: Chats
      description: Ask questions, check sources, and hand off tasks.
    projects:
      label: Projects
      description: Plan, assign, and review work with your team.
    automations:
      label: Automations
      description: Repeat workflows with triggers and approvals.
    knowledge:
      label: Knowledge
      description: Shared context for your team and agents.
    governance:
      label: Governance
      description: Review actions and control model usage.
  resource:
    compare:
      label: Compare
      description: Compare workspaces, workflows, and agent runtimes.
    useCases:
      label: Use cases
      description: Real work for your team and AI agents.
    hardware:
      label: Hardware
      description: Hardware for local AI inference.
    docs:
      label: Documentation
      description: Setup guides and product reference.
    changelog:
      label: Changelog
      description: What shipped, newest first.
    about:
      label: About us
      description: The Swiss company behind Tale.
  aboutUs: About us
  contactUs: Contact us
  requestDemo: Request a demo
  getStarted: Get started
legal:
  downloadPdf: Print or save as PDF
  downloadPdfAria: Print or save {title} as PDF
  notFound: This document is not available.
  documentTabsAria: Document sections
  tabs:
    data-processing-agreement: DPA
    technical-organizational-measures: TOM
footer:
  taleUi: Tale UI
  resources: Resources
  legal: Legal
  pricing: Pricing
  hardwarePricing: Hardware Pricing
  contact: Contact
  serviceAgreement: Service Agreement
  hardwareAgreement: Hardware Agreement
  privacyPolicy: Privacy Policy
  termsOfService: Terms of Service
  processingAgreement: Processing Agreement
  technicalOrganizationalMeasures: Technical & Organizational Measures
  homeAriaLabel: Tale home
  githubAriaLabel: GitHub
  llmsTxtLabel: llms.txt
  llmsFullTxtLabel: llms-full.txt
  platform: Platform
  company: Company
  changelog: Changelog
  features: Features
address:
  country: Switzerland
home:
  hero:
    title: Your team and agents, working together.
    subtitle: "Plan the work, give agents their tasks, and review what they deliver. Your team shares the brief, files, and conversation."
    ctaPrimary: Request a demo
    ctaSecondary: Self-host Tale
    eyebrow: Open-source project workspace
    proof: Shared tasks · Agent delegation · Parallel sandbox work · Open source
  demos:
    hero:
      label:
        "Illustrative demo: a chat question about support escalations is
        answered with two cited sources and the selected model."
      prompt: Summarize yesterday's support escalations and draft a status update.
      routedTitle: Model selected
      routedDetail: Auto picked Claude Sonnet for this thread.
      reply1: Yesterday logged 7 escalations — 5 resolved, 2 still open.
      reply2: Both open cases are delayed cargo-bike deliveries in the EU region.
      reply3: Ops has confirmed replacement units ship this week,
      reply4: so both contacts can be promised a resolution by Friday.
      citation1: q3-escalations.pdf
      citation2: eu-delivery-runbook.pdf
      thought: Thought for 1s
      composerModel: Claude Sonnet
      inputPlaceholder: Ask anything…
    connect:
      label:
        'Animated demo: the Agents list shows Claude Code, Codex, Hermes, Support
        Agent, and OpenClaw as installed rows with model chips.'
      windowTitle: Agents
      searchPlaceholder: Search agents
      addLabel: Agents
      colName: Name
      colModel: Model
      colStatus: Status
      statusReady: Ready
      agent1: Claude Code
      agent2: Codex
      agent3: Hermes
      agent4: Support Agent
      agent5: OpenClaw
      model1: Claude Opus
      model2: GPT-5.5
      model3: Claude Sonnet
      model4: Claude Sonnet
      model5: Claude Opus
    knowledge:
      label:
        'Animated demo: the Knowledge documents table indexes a PDF, a website, a
        product catalog, and a runbook, then shows indexed status badges.'
      windowTitle: Knowledge
      searchPlaceholder: Search documents
      addLabel: Knowledge
      colName: Name
      colType: Type
      colStatus: Status
      typePdf: PDF
      typeWebsite: Website
      typeCatalog: Catalog
      typeEntry: Entry
      statusIndexing: Indexing
      statusIndexed: Indexed
      source1: q3-escalations.pdf
      sourceType1: pdf
      source2: help.nordwind.eu
      sourceType2: website
      source3: Product catalog
      sourceType3: catalog
      source4: eu-delivery-runbook.pdf
      sourceType4: pdf
    automation:
      label: 'Animated demo: a workflow canvas runs Trigger → LLM → Condition →
        Action, then the Executions table records the run.'
      windowTitle: Automations
      kindTrigger: Trigger
      kindLlm: LLM
      kindCondition: Condition
      kindAction: Action
      statusCompleted: Completed
      statusAwaiting: Awaiting approval
      statusRunning: Running
      trigger: New email
      llm: Classify intent
      condition: Refund request?
      branchYes: Yes
      action: Draft reply
      actionAlt: Route to sales
      logTitle: Executions
      logColRun: Run
      logColStatus: Status
      logColDuration: Duration
      run1: '#482'
      runTone1: done
      duration1: 3.2s
      run2: '#483'
      runTone2: pending
      duration2: —
      run3: '#484'
      runTone3: running
      duration3: 1.1s
    govern:
      label:
        "Illustrative demo: an automation’s connector write waits for
        approval, then records the decision and execution outcome."
      approvalTitle: Send 42 refund emails to affected contacts?
      requester: Inbox triage · Draft reply
      approve: Approve
      reject: Reject
      approved: Approved
      auditTitle: Run journal
      audit1: Approved by anna@nordwind.eu
      audit2: 42 emails queued via Gmail
      audit3: Decision recorded in the audit log
      budgetLabel: Monthly budget
      budgetValue: CHF 412 / 1,000
    arena:
      label:
        'Animated demo: Chat Arena splits one prompt across two models so you can
        compare Claude Sonnet and GPT-5.5 side by side.'
      prompt: Draft a contact reply for the delayed EU shipment.
      modelA: Claude Sonnet
      modelB: GPT-5.5
      vs: vs
      replyA1: Apologize for the delay and confirm replacement units ship this week.
      replyA2: Offer a Friday resolution date and a tracking link once Ops posts it.
      replyA3: Close with the escalation policy link so the contact can share it
        internally.
      replyB1:
        Acknowledge the delay, cite the escalation policy, and set Friday as
        the commit.
      replyB2: Include a short next-step checklist the contact can forward internally.
      replyB3: Offer a callback window if the tracking link is not posted by Thursday.
    projects:
      label:
        'Animated demo: the Projects list shows scoped workspaces with agent
        counts and members.'
      windowTitle: Projects
      searchPlaceholder: Search projects
      newProject: New project
      colName: Name
      colAgents: Agents
      colMembers: Members
      project1: Customer Support
      project2: Engineering
      project3: Sales Research
      project4: Ops & Logistics
      agents1: 3 agents
      agents2: 5 agents
      agents3: 2 agents
      agents4: 4 agents
      members1: '8'
      members2: '14'
      members3: '5'
      members4: '11'
    tasks:
      windowTitle: "Tasks"
      colTodo: "To do"
      colInProgress: "In progress"
      colInReview: "In review"
      colDone: "Done"
      working: Working
      label: "Animated demo: a shared project board with To do, In progress, In review, and Done columns. People and agents own tasks, and an agent is working on one card."
      id1: TEAM-14
      title1: Plan the campaign brief
      assignee1: Unassigned
      id2: TEAM-15
      title2: Review customer feedback
      assignee2: Mira
      id3: TEAM-12
      title3: Build the customer portal
      assignee3: Builder Agent
      id4: TEAM-11
      title4: Review the research report
      assignee4: Mira
      id5: TEAM-9
      title5: Update the team playbook
      assignee5: Docs Agent
    sandbox:
      filesTab: Files
      liveTab: Live
      treeRoot: workspace
      liveHint: Live browser view
      label:
        'Animated demo: an agent creates a web page, shows its HTML in the
        Files pane, then opens the page in the Live browser view.'
      prompt:
        Build a small project page and open it in the browser so I can review
        it.
      agent: Claude Code
      model: Claude Opus
      reply:
        Created index.html and a local preview. Review the source in Files and
        the page in Live.
      file1: index.html
      file2: styles.css
      file3: tests/preview.spec.ts
      file4: README.md
      activeFile: index.html
      code1: "<main><h1>Northwind</h1><p>Customer portal</p>"
      code2: "<section><h2>Brief</h2><p>Scope and goals</p></section>"
      code3: "<section><h2>Files</h2><p>Source and tests</p></section>"
      code4: "<section><h2>Review</h2><p>Ready for feedback</p></section></main>"
      browserUrl: http://127.0.0.1:5173
      browserTitle: Project preview · localhost
      previewTitle: "Northwind"
      previewDescription: "Customer portal"
      previewItem1: "Brief"
      previewDetail1: "Scope and goals"
      previewItem2: "Files"
      previewDetail2: "Source and tests"
      previewItem3: "Review"
      previewDetail3: "Ready for feedback"
  tour:
    title: From a shared task to a reviewed result
    subtitle:
      Give work an owner, give agents the context to act, and keep your
      teammates involved as the project moves forward.
    connect:
      eyebrow: Agents & connectors
      title: "Delegate work to your agents"
      description: "Give agents their instructions and tools. Follow the work in persistent sandboxes, then inspect the files they deliver."
    pool:
      eyebrow: Knowledge
      title: "Give work the right context"
      description: "Connect project files and shared knowledge. Inspect the sources agents use."
    delegate:
      eyebrow: Automations
      title: "Repeat useful workflows"
      description: "Connect agent steps, conditions, and actions. Inspect each run."
    govern:
      eyebrow: Approvals & audit
      title: "Review and approve"
      description: "Review deliverables, approve configured actions, and follow the audit trail."
    arena:
      eyebrow: Chat Arena
      title: "Compare model answers"
      description: "Ask two models the same question in Arena. Choose the reply that fits."
    projects:
      eyebrow: Projects
      title: |-
        Plan the work
        with your team
      description: Bring teammates, tasks, files, and conversations into a project.
        Assign work to a person or an agent and follow its progress on the
        board.
    explore: Explore {module}
  tagline:
    eyebrow: Your workspace, your choice
    title: Your Infrastructure. Your Data. Your Rules.
    subtitle:
      Choose where your workspace runs and which models your agents use.
    pillars:
      selfHosted:
        title: Self-hosted by default
        description:
          Run Tale on your own infrastructure or choose managed hosting. Local
          models keep inference local; external providers, connectors, and
          tools follow the connections you configure.
      security:
        title: Security built in
        description: Manage members, roles, single sign-on, budgets, and configured
          guardrails in the same product. Use audit records and run history to
          investigate what happened.
      openSource:
        title: Fully open source
        description:
          The entire codebase is public and open source. Read it, audit
          it, change what you need — without asking anyone.
  connectors:
    title: Plugged into the tools you already run
    subtitle:
      Connect the services your projects depend on. Configure credentials
      once, then grant the access agents and workflows need for their work.
  agents:
    title: Your agents, your choice
    subtitle: Use your preferred runtime with your own subscription or API-billed models.
  faq:
    eyebrow: FAQ
    title: Frequently Asked Questions
    stillQuestions: Still have questions?
    contactTeam: Contact the team
    whatIsTale:
      q: What is Tale?
      a:
        Tale is an open-source project workspace for teams and AI agents. Plan
        tasks with teammates, delegate work to agents, and review the results
        in the same place. It combines task boards, agent sandboxes, shared
        knowledge, and workflows. Self-hosting and private models are
        available when you need them.
    openSource:
      q: Is Tale open source?
      a: Yes. Tale is open source and the full feature set ships in the free
        Community edition — Enterprise adds support and services, not features.
    onPrem:
      q: Can Tale run fully on-premises or air-gapped?
      a:
        Tale can run on your own infrastructure with local models. An offline
        deployment also needs local dependencies and restricted outbound
        connections. Cloud models, external tools, and connectors can send
        data outside your network when enabled; self-hosting alone does not
        make them local.
    enterprisePricing:
      q: How does Enterprise pricing work?
      a:
        Enterprise is billed per user/month, with two months free when paying yearly.
        Storage beyond the included quota is billed per TB/month. The Community
        edition stays free for self-hosted use.

    byoModels:
      q: Do I have to bring my own AI models?
      a:
        No. You can bring your own models, but it's not required. Tale supports most
        major AI providers without markup — cloud model usage is billed at your
        provider's rates and is not included in the plans.




    whoFor:
      q: Who is Tale for?
      a: Tale is for teams that want to organize work for AI agents and
        collaborate with them. Technical members configure agents, tools, and
        repository access; other teammates can describe tasks, discuss
        progress, and review results. Use it for software projects, marketing
        campaigns, research, reports, or recurring operational work. What an
        agent can do depends on its tools and permissions.
    agentSubscriptions:
      q: Can I use my existing agent subscription?
      a: Yes, when the agent runtime supports that subscription. Bring your own token
        or supported login credential and choose the matching runtime. You can
        also use API-backed models. Subscription calls follow the provider’s
        limits and bypass Tale’s gateway metering and spending caps; check
        that usage with the provider.
  cta:
    title: Bring your next project to Tale
    description:
      Start a self-hosted workspace or bring a real task to a demo. See how
      your team and AI agents can work through it together.
    primary: Request a demo
    getStarted: Get started
    terminalTitle: Self-host in four commands
    copy: Copy
    copied: Copied
    docsLink: Full install guide
complianceTrust:
  certifications:
    iso27001: Ruler GmbH · ISO 27001
    mit: Open source
    openSource: Open Source
pricing:
  title: How much does Tale cost?
  description:
    Tale Enterprise is CHF 12 (EUR 14) per user/month, with two months
    free on yearly billing. The open-source Community edition is free to
    self-host.
  note: All product features are included in both Community and Enterprise.
    Additional cloud AI model usage is billed at your provider's rates.
  popular: Popular
  planIncludes: 'Plan includes:'
  billing:
    ariaLabel: Billing period
    monthly: Monthly
    yearly: Yearly (2 months free)
  billingNote:
    monthly: Billed monthly
    yearly: Billed yearly · 2 months free
  users:
    label: Number of users
    minTooltip: Minimum is 25 users
  region:
    ariaLabel: Currency
    CH: CHF
    DE: EUR
  community:
    deployment: Self-hosted only
    price: Free
    priceSuffix: ''
    tagline: Every product feature, free to self-host — support happens on GitHub.
    cta: Get started
    feature1: Self-hosted
    feature2: Open source
    feature3: Community-maintained (GitHub)
    feature4: Community support (GitHub)
  enterprise:
    deployment: Cloud or self-hosted
    priceSuffix: /month + VAT
    storageAddOn: 1 TB + {price}/TB/month additional storage
    userBreakdown: '{count} users × {perUser}/user/month'
    tagline: Everything in Community, professionally run — installation,
      maintenance, and support, in the cloud or on your hardware.
    cta: Contact us
    feature1: Self-hosted or cloud
    feature2: Professional operation by ISO 27001-certified Ruler GmbH
    feature3: Data processing agreement (DPA)
    feature4: Email, phone & remote support
    feature5: Installation & ongoing maintenance
    feature7: Influence on product roadmap
  compare:
    title: Compare Plans
    subtitle: Breakdown of each plan
    categories:
      deployment: Deployment
      compliance: Compliance
      support: Support
      installation: Standard installation
      maintenance: Maintenance
      customDevelopment: Custom development
      consulting: Consulting
      roadmap: Influence on product roadmap
    values:
      installationFee:
        CH: CHF 1'990
        DE: EUR 2,290
      hourlyRate:
        CH: CHF 180 / h
        DE: EUR 200–260 / h
    sections:
      services: Professional services
      other: Other
    rows:
      selfHosted: Self-hosted
      cloud: Cloud
      localDataCenters: Local data centers (CH & EU)
      dpa: Data processing agreement (DPA)
      iso: ISO 27001-certified operation
      piiRedaction: PII redaction
      customDpa: Custom DPA
      customDpaInfo: We also accept custom Data Processing Agreements with our customers.
      emailSupport: Email support
      emailSupportInfo: 'Replies during business hours: Mon–Fri, 08:00–17:00 (CET/CEST).'
      phoneSupport: Phone support
      phoneSupportInfo: 'Available during business hours: Mon–Fri, 08:00–17:00 (CET/CEST).'
      remoteSupport: Remote support
      remoteSupportInfo: 'Available during business hours: Mon–Fri, 08:00–17:00 (CET/CEST).'
    cellLabels:
      yes: Included
      no: Not included
  extras:
    hardware:
      title: Hardware
      prefix: For self-hosting, we offer
      linkLabel: flexible hardware options
      suffix: ' — renting, buying, or financing.'
    cloudProviders:
      title: Cloud AI providers
      description:
        We support OpenAI, Anthropic, Gemini, and other major cloud AI
        providers in CH & EU — no markup; usage is billed at the provider's
        rates.
  terms:
    title: Terms
    prefix: All configurations are subject to our
    linkLabel: service agreement
    suffix: .
hardwarePricing:
  title: What does Tale AI hardware cost?
  description:
    Silent inference hardware from single nodes to racks — rent, lease,
    or buy. Deploy on-site with remote maintenance, or host in secure data
    centers.
  popular: Popular
  configIncludes: 'Configuration includes:'
  billing:
    ariaLabel: Payment option
    leasing: Leasing
    buying: Buying
    termAriaLabel: Leasing term
    termHeading: 'Months:'
  modesAriaLabel: Deployment mode
  deploymentNote: All hardware configurations are silent (noise-free) and can be
    deployed at your site and maintained remotely by our team, or hosted in our
    secure data centers.
  tiers:
    quality:
      priceSuffix: + VAT/month
      buySuffix: + VAT
      tagline: Tuned for accuracy and consistent output.
      nodeTagline: Made for accuracy and consistent output.
      cta: Contact us
      highlightQuality: Quality
      highlightSpeed: Standard
    hybrid:
      priceSuffix: + VAT/month
      buySuffix: + VAT
      tagline: Balances accuracy with speed.
      nodeTagline: Combines local data control with high-speed inference.
      cta: Contact us
      highlightQuality: High
      highlightSpeed: High
    speed:
      priceSuffix: + VAT/month
      buySuffix: + VAT
      tagline: Tuned for low-latency replies.
      nodeTagline: Made for low-latency replies.
      cta: Contact us
      highlightQuality: Standard
      highlightSpeed: Quality
    rack:
      priceSuffix: + VAT/month
      buySuffix: + VAT
      tagline:
        Multi-node performance packaged in a single, secure chassis — with
        confidential computing.
      nodeTagline:
        Multi-node performance packaged in a single, secure chassis — with
        confidential computing.
      cta: Contact us
      highlightQuality: High
      highlightSpeed: High
  compare:
    title: Compare Configurations
    subtitle: Breakdown of each configuration
    categories:
      model: Recommended AI model
      modelInfo:
        Suggested model for a multi-node setup. Single-node configurations
        can run smaller variants — your team picks the actual model at deploy
        time.
      size: Size
      sizeInfo: Approximate dimensions — actual size may vary slightly.
      gpu: Chip (GPU)
      cpu: Chip (CPU)
      ram: AI RAM
      ramInfo:
        Fast memory used for AI workloads, e.g. GDDR7 video memory or Unified
        Memory (UMA).
      umaInfo: Unified Memory Architecture — RAM shared between CPU and GPU on a
        single chip (Apple Silicon).
      gddr7Info:
        7th-generation graphics DDR — high-bandwidth video memory on the GPU
        board, optimised for AI inference workloads.
      ddr5Info: Fifth-generation DDR SDRAM (faster, more efficient than DDR4).
      ddr5eccInfo:
        DDR5 with error-correcting code — server-grade memory that detects
        and corrects single-bit faults.
      zen4Info: AMD's 4th-generation x86 CPU microarchitecture (2022).
      zen5Info: AMD's 5th-generation x86 CPU microarchitecture (2024).
      int4Info:
        4-bit integer quantization — trades a small amount of model precision
        for ~4× less memory.
      m2nvmeInfo: m.2 NVMe SSDs in a ZFS RAID pool — fast, redundant storage.
      nqInfo: Node Quality
      naInfo: Node Application
      nsInfo: Node Speed
      mqInfo: Multi-node Quality
      mhInfo: Multi-node Hybrid
      msInfo: Multi-node Speed
      srInfo: Server Rack
      systemRam: RAM
      ssd: SSD
      hdd: HDD
      cables: Cables & network equipment
      confidentialComputing: Confidential computing
      version: Product version
      productNumber: Product number
      composition: Composition
    cellLabels:
      yes: Included
      onRequest: Available on request
      notAvailable: Not available
    sections:
      specifications: Specifications
      other: Other
  extras:
    software:
      title: Software & services
      prefix: For installation, support, and professional services, we offer
      linkLabel: flexible options
      suffix: .
  terms:
    title: Terms
    prefix: All configurations are subject to our
    linkLabel: hardware agreement
    suffix: .
  warranty:
    title: Warranty
    content: 24 months manufacturer warranty (extensible to 36 months on request)
  metrics:
    quality: Output quality
    speed: Token speed
    storage: Data storage
requestDemo:
  eyebrow: Demo
  title: See Tale in action
  paragraph1:
    'A guided demo is the product live, not a slide deck: agents docked,
    replies grounded in your kind of knowledge, and an automation pausing for
    approval — on a self-hosted instance.'
  paragraph2: Tell us what your team runs today and what must never leave your
    network. You'll hear back within one business day with a time and a plan
    that fits.
  fieldName: Name
  fieldPhone: Phone
  fieldCompany: Company
  fieldInterests: Interested in
  fieldMessage: Additional comment
  placeholderName: Enter your full name
  placeholderEmail: Enter your email address
  placeholderPhone: Enter your phone number
  placeholderCompany: Enter your company name
  placeholderMessage: Describe your project. All project details shared with us
    are kept confidential.
  interests:
    enterprise: Enterprise solutions
    professionalServices: Professional services
    customAiTraining: Custom AI model training
    aiHardware: AI hardware
  submit: Get in touch
contact:
  eyebrow: Contact
  title: Contact us
  description:
    Discuss a project your team wants to delegate to AI agents, or ask about
    deployment, Enterprise, and support. Tell us how your team works today.
  fieldName: Name
  fieldCompany: Company
  fieldMessage: Message
  placeholderName: Enter your full name
  placeholderEmail: Enter your email address
  placeholderCompany: Enter your company name
  placeholderMessage: Share the details that matter — what you're trying to do,
    what's blocking, the scale you're at. Everything you write here stays
    confidential.
  submit: Get in touch
forms:
  email: Email
  honeypotLabel: Website
  privacyPrefix: I confirm that I have read the
  privacyLink: Privacy Policy
  privacyRequired: Confirm that you have read the Privacy Policy.
  validation:
    nameRequired: Enter your name.
    nameTooLong: Use 2,000 characters or fewer for your name.
    emailRequired: Enter your email address.
    emailInvalid: Enter a valid email address.
    emailTooLong: Use an email address with 2,000 characters or fewer.
    companyTooLong: Use 2,000 characters or fewer for the company name.
    messageRequired: Enter your message.
    messageTooShort: Tell us a little more. Use at least 10 characters.
    messageTooLong: Keep your message to 2,000 characters or fewer.
    phoneInvalid: Enter a phone number using digits, spaces, +, - or parentheses.
    phoneTooShort: Enter a phone number with at least 4 digits.
    phoneTooLong: Use 32 characters or fewer for the phone number.
    invalid: Check this value and try again.
  errors:
    generic: Something went wrong. Try again.
    rateLimited: Too many submissions. Wait a moment, then try again.
    notConfigured: This deployment has no form delivery configured yet, so your
      message was not sent. Please use another channel to reach the team.
    serverUnavailable: We couldn't send your message right now. Please try again in a few minutes.
    tooFast: Hold on — review your details before submitting.
  success:
    title: Thanks — we got it
    description: We'll be in touch within one business day.
    sendAnother: Send another
about:
  hero:
    eyebrow: About us
    title: Who is behind Tale?
    description: Tale is built by Ruler GmbH in Spiez, Switzerland. We build an
      open-source project workspace where people and AI agents solve problems
      together. Our ISO 27001 certification covers Tale Enterprise and
      professional services.
    ctaContact: Contact us
    ctaGithub: View the code
  facts:
    founded:
      label: Founded
      value: '2020'
    location:
      label: Based in
      value: Spiez, Switzerland
    certified:
      label: Certified
      value: Ruler GmbH · ISO 27001
    license:
      label: License
      value: Open source
  story:
    heading: Our story
    p1: Ruler GmbH was founded in Spiez, in the Canton of Bern, in 2020 by
      Yannick Monney. From day one the company has been Swiss-based and
      vendor-neutral — no ties to a cloud or model provider.
    p2: We build Tale for companies that want AI agents to work alongside their
      teams. Work needs owners, context, coordination, and a place to review
      the result. A shared project workspace brings those pieces together.
    tagline: Your Infrastructure. Your Data. Your Rules.
    p3: Tale brings project boards, agent sandboxes, files, knowledge, and
      workflows into one product. Teams can assign tasks, follow agent
      progress, and review deliverables together, with a choice of runtimes,
      models, and deployment.
  product:
    heading: What we build
    description: "Tale is an open-source workspace for teams and AI agents: one place to
      plan projects, delegate tasks, and review results."
    orchestrator:
      title: People and agents on the same project
      body: Assign work to teammates or project agents. Give agents instructions,
        tools, and a supported runtime, then follow their tasks and inspect
        what they produce.
    openSource:
      title: Open source, no asterisks
      body: The entire codebase is public and open source on GitHub. The
        free Community edition ships identical features; Enterprise adds
        support and services at CHF 12 (EUR 14) per user/month.
    deployment:
      title: Your infrastructure or ours
      body: Self-host on your own infrastructure or choose managed hosting in
        Switzerland or the EU. Choose local or cloud models and configure
        which external services the workspace can reach.
    beyond:
      title: Beyond software
      body: We supply AI hardware to rent, lease, or buy for teams running models on their own infrastructure.
  work:
    heading: How we work with companies
    description: Putting agents to work takes a clear process as well as software. We
      help teams prepare their sources, define useful tasks, and establish how
      people review the results.
    dataQuality:
      title: Data worth pointing agents at
      body: We help structure sources, ownership, and provenance so people and
        agents can find and check the relevant evidence.
    regulated:
      title: At home in regulated environments
      body: We help teams evaluate deployment, access, and review requirements.
        Ruler GmbH’s ISO 27001 certification covers Tale Enterprise and
        professional services; request the scope and evidence relevant to your
        organization.
    edge:
      title: An edge that stays yours
      body: Keep your team’s project context, workflows, and review history
        together as you add agents. The open-source codebase and a choice of
        runtimes and deployment let you adapt Tale to how your company works.
    tailored:
      title: Solutions shaped to your goals
      body: No two organizations adopt AI the same way. We build the agents,
        automations, knowledge, and hardware around what you are trying to
        achieve — and leave out what you don't need.
  values:
    heading: What we stand for
    privacy:
      title: Data stays yours
      body: Keep deployment and model choices in your hands. Use local inference
        when needed and review the data flows of any external services you
        connect.
    neutrality:
      title: Independent & vendor-neutral
      body: Swiss-based and vendor-neutral — no ties to a cloud or model
        provider, so your deployment answers only to you.
    transparency:
      title: Transparent by default
      body: A public open-source codebase and a company you can look up in
        the Swiss commercial register. What we ship is what you can read.
    control:
      title: Humans stay in charge
      body: People define the work, grant agent access, and configure review and
        approval policies. Teams can inspect deliverables and run history
        before deciding what comes next.
  cta:
    title: Talk to the team
    description: Questions about Tale, deployment, or working with Ruler GmbH?
      Book a demo or reach us through the contact form.
featureShared:
  ctaPrimary: Request a demo
  ctaSecondary: Contact us
  ctaGetStarted: Get started
  ctaTitle: See your team’s next workflow in Tale
  ctaDescription: Bring a task to a guided demo, or start a workspace and try it with your
    own agents.
  relatedHeading: Related modules
  docsHeading: Read the docs
platformHub:
  title: How does the Tale platform work?
  description: Tale brings teammates and AI agents into one project workspace. Plan work
    on task boards, delegate to agents running in sandboxes, and review the
    results with the context and files that produced them.
  modulesHeading: Platform modules
  modulesDescription: Projects organize the work. Agents execute assigned tasks. Chat and
    Knowledge supply context. Automations make recurring steps repeatable,
    while Governance configures access and review.
  tour:
    heading: How the platform fits together
    description:
      Plan a project, equip agents for its tasks, and review their results.
      Knowledge, chat and repeatable workflows support the work your team
      coordinates.
    stages:
      - id: agents
        eyebrow: Agents
        title: |-
          Agents for
          your project tasks
        description:
          Configure project agents with a supported runtime, model and tools.
          Assign them tasks and follow their progress with your teammates.
      - id: projects
        eyebrow: Projects
        title: Scope by team
        description:
          Bring tasks, agents, files and instructions into a project. Choose
          its audience and explicitly share the chats your teammates should
          read.
  faq:
    heading: Platform FAQ
    items:
      - q: What is the platform in Tale?
        a:
          Tale is an open-source project workspace for teams and AI agents.
          Plan tasks, delegate work, follow progress and review results, with
          shared context and repeatable workflows.
      - q: Which modules does Tale include?
        a: Chat, Projects, Knowledge, Agents, Automations, and Governance — with
          Projects scoping work, files, and agents by team.
      - q: Is Tale open source?
        a:
          Yes. Community is open source and free to self-host. Enterprise adds support
          and services on the same codebase.
      - q: Where should I start?
        a:
          Start with a project and one task whose result you can judge. Add
          the relevant files, assign a configured agent, start it, and review
          what it delivers.
  demos:
    connect:
      label:
        'Animated demo: the hub Agents sampler — Orchestrator Agent, Support
        Agent, Docs Agent, Invoice Agent, and Policy Agent, each with its
        model.'
      agent1: Orchestrator Agent
      model1: Claude Sonnet
      agent2: Support Agent
      model2: Claude Sonnet
      agent3: Docs Agent
      model3: Claude Opus
      agent4: Invoice Agent
      model4: GPT-5.5
      agent5: Policy Agent
      model5: Claude Opus
    projects:
      label:
        'Animated demo: hub Projects sampler — Platform core, Customer pilots,
        Partner enablement, and Internal ops, each with its own roster.'
      project1: Platform core
      agents1: 6 agents
      members1: '12'
      project2: Customer pilots
      agents2: 3 agents
      members2: '7'
      project3: Partner enablement
      agents3: 2 agents
      members3: '5'
      project4: Internal ops
      agents4: 4 agents
      members4: '9'
platformAgents:
  eyebrow: Agents
  title: What are agents in Tale?
  description: Project agents work on tasks alongside your team. Give each one
    instructions, a supported runtime, a model and tools, then follow its
    sandbox work and review its reports and files.
  capabilities:
    heading: What can agents do?
    description:
      Configure reusable workers for project tasks, from research and document
      preparation to code changes and review.
    items:
      - title: A defined role and runtime
        body:
          Choose a runtime, a compatible model and provider, standing
          instructions, and the equipment the work needs.
        docsLabel: Agent concepts
      - title: Supported agent runtimes
        body:
          Bind Claude Code, Codex, Hermes, and OpenClaw as the agent runtime a project
          agent or automation agent node runs on.
        docsLabel: Agent runtimes
      - title: Delegation between agents
        body:
          An equipped manager agent can start other agents on ready tasks in
          the same project, within delegation, access and capacity limits.
        docsLabel: Task automation
      - title: Skills as reusable bundles
        body:
          Give agents reusable instructions and supporting files as skills.
          Equip them for tasks such as preparing documents or reviewing
          changes.
        docsLabel: Project agents
      - title: Tools for project context
        body:
          Grant the platform tools an agent needs to find tasks, read files
          and retrieve knowledge. Access rules still apply to the sources it
          uses.
        docsLabel: Knowledge
  faq:
    heading: Agents FAQ
    items:
      - q: What is an agent in Tale?
        a:
          A project agent is a reusable worker in one project. Its runtime,
          model, instructions and tools determine how it approaches tasks.
          Reports and produced files stay with the task for review.
      - q: Can we bring our own subscriptions or API keys?
        a:
          Yes. Connect your API credentials, or use a supported Anthropic
          subscription with Claude Code or ChatGPT subscription with Codex for
          task and automation agents. An administrator configures the
          credentials or token broker. Chat needs API credentials;
          subscription availability and limits depend on your provider and
          plan.
      - q: When should I use an agent instead of a workflow?
        a:
          Use a project agent for an outcome that needs tools, files or
          several turns of work. Use an automation when you can define
          repeatable steps, conditions or triggers.
      - q: Can each agent use a different model?
        a:
          Yes. Choose a model and provider for each project agent, subject to
          runtime compatibility and the models available in your organization.
          Configuration changes apply to later runs.
  docs:
    - label: Agent concepts
      path: /platform/agents/concepts
    - label: Project agents
      path: /platform/projects/project-agents
    - label: Agent runtimes
      path: /platform/agents/harnesses
    - label: Task automation
      path: /platform/projects/task-automation
    - label: Knowledge
      path: /platform/knowledge/overview
  tour:
    heading: How agents show up in Tale
    description:
      Configure agents for a project, give them the tools and context they
      need, and follow the work through its reports and deliverables.
    stages:
      - id: projects
        eyebrow: Projects
        title: |-
          Scope agents
          by team
        description:
          Each project has its own agent configuration. Project access
          determines who can see the agents and their tasks.
      - id: knowledge
        eyebrow: Knowledge
        title: |-
          Ground every agent
          in what you know
        description:
          Attach the task brief and reference files, and grant tools for
          accessible company knowledge. Give the agent clear instructions on
          which sources to use.
  demos:
    knowledge:
      label: "Illustrative demo: reference material an agent can use when equipped
        with the appropriate knowledge tools and access."
      source1: support-macros.pdf
      sourceType1: pdf
      source2: Brand voice
      sourceType2: entry
      source3: agents.nordwind.eu
      sourceType3: website
      source4: returns-playbook.pdf
      sourceType4: pdf
    projects:
      label:
        'Animated demo: Projects scoped so Support and Platform each keep their
        own agent stack and members.'
      project1: Support desk
      agents1: 4 agents
      members1: '9'
      project2: Platform build
      agents2: 6 agents
      members2: '12'
      project3: Docs & enablement
      agents3: 2 agents
      members3: '4'
      project4: Release train
      agents4: 3 agents
      members4: '7'
platformChat:
  eyebrow: Chats
  title: Ask, compare, then hand work to an agent
  description: Use chat to understand a problem, work with project sources, and draft a
    brief. When the work needs files, code, or tools, create a project task
    and assign an agent.
  capabilities:
    heading: What does Chat include?
    description:
      Explore a question, check the sources, and prepare the next task.
    items:
      - title: Choose a model
        body:
          Pick an available model and provider, or let Auto select a model for
          each message within your organization’s rules.
        docsLabel: Chat basics
      - title: Inspect the sources
        body:
          Open the documents and pages used in an answer. Check that the cited
          material supports the conclusion.
        docsLabel: Chat basics
      - title: Compare two models in Arena
        body:
          Send the same prompt and starting context to two models. Read both
          replies and choose how to continue.
        docsLabel: Arena mode
      - title: Bring the relevant files
        body:
          Attach supported documents, images, or recordings. Processing and
          model capabilities determine what the assistant can read.
        docsLabel: Attachments
      - title: Move from a question to a task
        body:
          Create a project task from the conversation when you need an agent
          to produce files or perform work in a sandbox.
        docsLabel: Project agents
  faq:
    heading: Chat FAQ
    items:
      - q: What is Chat in Tale?
        a:
          Chat is the built-in assistant for questions, source retrieval, and
          text drafts. It can use accessible company knowledge and the current
          project’s context.
      - q: Do I need to configure a project agent for chat?
        a:
          No. Chat uses Tale’s built-in assistant and your chosen model.
          Project agents are separate workers that you assign to project
          tasks.
      - q: What is Arena mode?
        a:
          Arena sends one prompt to two models with the same starting context.
          You compare their replies and record a verdict before continuing the
          conversation.
      - q: How do I turn a chat into work?
        a:
          Create a project task from the conversation and assign an agent.
          Chat itself does not run code, change connected systems, or produce
          file deliverables. Task progress and results stay linked to the
          conversation.
  docs:
    - label: Chat overview
      path: /platform/chat/overview
    - label: Chat basics
      path: /platform/chat/basics
    - label: Arena Mode
      path: /platform/chat/arena-mode
    - label: Project agents
      path: /platform/agents/concepts
    - label: Attachments
      path: /platform/chat/attachments
  tour:
    heading: How Chat works in Tale
    description:
      Discuss the problem in chat, then use project tasks and automations for
      the work that follows.
    stages:
      - id: projects
        eyebrow: Projects
        title: |-
          Keep the discussion
          with the work
        description:
          Use project chats for shared context and choose which conversations
          to share with project members. Tasks, files, and instructions stay
          in the same project.
      - id: knowledge
        eyebrow: Knowledge
        title: |-
          Check answers
          against their sources
        description:
          Chat can retrieve accessible knowledge and project files. Open the
          sources behind an answer and check them before using the result.
  demos:
    arena:
      label:
        "Illustrative demo: two models draft an announcement from the same
        release brief in Arena."
      prompt: Draft an announcement from the attached release brief.
      modelA: Claude Sonnet
      modelB: GPT-5.5
      replyA1:
        Lead with the main improvement described in the brief.
      replyA2: Explain who it helps and cite the supporting release note.
      replyA3: Close with the documented next step.
      replyB1: Start with the customer problem the release addresses.
      replyB2: List the changes and link each one to the source.
      replyB3: End with the rollout details from the brief.
    projects:
      label:
        "Illustrative demo: projects keep related chats, tasks, files, and
        agents together."
      project1: Support threads
      agents1: 3 agents
      members1: '8'
      project2: Engineering chat
      agents2: 5 agents
      members2: '11'
      project3: Comms drafts
      agents3: 2 agents
      members3: '4'
      project4: Pilot war-room
      agents4: 3 agents
      members4: '6'
    knowledge:
      label:
        'Animated demo: the sources Chat retrieves for a grounded reply — the
        release notes PDF, the changelog crawl, the announcement template, and
        the brand-voice entry.'
      source1: release-notes-2.4.pdf
      sourceType1: pdf
      source2: changelog.nordwind.eu
      sourceType2: website
      source3: Announcement template
      sourceType3: entry
      source4: Tone guide
      sourceType4: entry
platformAutomations:
  eyebrow: Automations
  title: What are Automations in Tale?
  description: Automations make recurring project work repeatable. Connect model calls,
    agent steps and actions into a versioned workflow, test it, and run it
    manually or through a configured trigger.
  capabilities:
    heading: Define and repeat the process
    description:
      Keep the steps, saved versions, triggers and run history together so the
      team can inspect how recurring work is performed.
    items:
      - title: Saved versions and deployment
        body:
          Save a new version when the workflow changes. Deploy the version
          that future live runs should use; existing runs keep the version
          they started with.
        docsLabel: Automation concepts
      - title: Typed workflow steps
        body:
          Combine model calls, sandboxed agent work, data transformations,
          subautomations and connected actions. Add conditions or iteration
          where supported.
        docsLabel: Workflow editor
      - title: Triggers that fit the job
        body:
          Schedules (cron), webhooks, platform events, or a manual test run from the
          editor.
        docsLabel: Triggers
      - title: Execution logs
        body:
          Inspect a run’s status, inputs, outputs and step trace. Review
          recorded connector effects when checking what happened.
        docsLabel: Execution logs
      - title: Approvals in the loop
        body:
          Connector policies can pause protected writes for approval. An agent
          step can also ask a person for missing information before
          continuing.
        docsLabel: Automation concepts
  faq:
    heading: Automations FAQ
    items:
      - q: What is an automation in Tale?
        a: An automation defines a repeatable workflow with inputs, steps and
          outputs. Saved versions, deployment, triggers and run history let
          your team test and operate that process.
      - q: Is there a standalone workflow product?
        a:
          No. A workflow lives inside its automation. The automation's Editor tab is
          where you meet the graph.
      - q: When should I use an automation instead of an agent?
        a:
          Use an automation for repeatable steps, conditions, schedules or
          connected actions. Use a project agent when a task needs an outcome
          that you will review. An automation can include agent work.
      - q: Can I test a workflow before it runs on a schedule?
        a:
          Yes. Test a saved version before deploying it or enabling a trigger.
          Mock runs simulate connector operations; live runs can affect
          connected systems and may require approval.
  docs:
    - label: Automation concepts
      path: /platform/automations/concepts
    - label: Workflow editor
      path: /platform/automations/editor
    - label: Triggers
      path: /platform/automations/triggers
    - label: Execution logs
      path: /platform/automations/execution-logs
    - label: Built-in automations
      path: /platform/automations/builtin
  tour:
    heading: How automations run in Tale
    description:
      Define the process, choose the steps and data it needs, and inspect each
      run. Configure approval policies for protected connector writes.
    stages:
      - id: govern
        eyebrow: Approvals
        title: |-
          Human-in-the-loop
          before it ships
        description:
          When policy requires approval, a connector write waits for a person.
          Approval lets the run attempt the action; rejection blocks it.
      - id: agents
        eyebrow: Agents
        title: |-
          Give an agent
          a workflow step
        description:
          Configure an agent step with its own runtime, model, instructions
          and equipment. Use a model call when a single response is enough.
  demos:
    automation:
      label:
        'Animated demo: an invoice pipeline extracts line items, holds amounts
        over CHF 5,000 for approval, and files the rest to the ERP; the
        Executions table records each run.'
      trigger: New invoice
      llm: Extract items
      condition: Over CHF 5,000?
      branchYes: Yes
      action: Await approval
      actionAlt: File to ERP
      run1: '#1207'
      runTone1: done
      duration1: 4.8s
      run2: '#1208'
      runTone2: pending
      duration2: —
      run3: '#1209'
      runTone3: running
      duration3: 1.2s
    govern:
      label:
        "Illustrative demo: an invoice workflow waits for a connector write
        approval on its run detail. After approval, the execution result is
        recorded."
      approvalTitle: File 7 invoices totalling CHF 23,410 to the ERP?
      requester: 'Invoice pipeline · Run #1208'
      audit1: Approved by finance@nordwind.eu
      audit2: 7 invoices filed to the ERP
      audit3: 'Run #1208 completed in 41s'
      budgetValue: CHF 1,240 / 2,500
    connect:
      label:
        "Illustrative demo: named agents for invoice, vendor, ERP, matching
        and exception tasks. Workflow agent steps have their own
        configuration."
      agent1: Invoice Agent
      model1: Claude Sonnet
      agent2: Vendor Agent
      model2: GPT-5.5
      agent3: ERP Agent
      model3: Claude Opus
      agent4: Matching Agent
      model4: Claude Sonnet
      agent5: Exceptions Agent
      model5: GPT-5.5
    projects:
      label: "Illustrative demo: projects that organize the tasks and agents
        associated with recurring workflows."
      project1: Invoice ops
      agents1: 3 agents
      members1: '6'
      project2: Vendor intake
      agents2: 2 agents
      members2: '4'
      project3: Engineering CI
      agents3: 4 agents
      members3: '10'
      project4: Nightly reviews
      agents4: 2 agents
      members4: '5'
platformKnowledge:
  eyebrow: Knowledge
  title: What is Knowledge in Tale?
  description: Keep documents, short facts, website content and structured records
    available to your team. Chat and equipped project agents can retrieve
    accessible sources to support answers and task work.
  capabilities:
    heading: What lives in Knowledge?
    description: Prepare useful sources for retrieval and check the evidence behind an
      answer or deliverable.
    items:
      - title: Documents with an indexing pipeline
        body:
          Upload supported files and check their indexing status. Indexed text
          can be retrieved with source references.
        docsLabel: Documents
      - title: Knowledge entries
        body:
          Save a concise fact under a stable topic. Update the current entry
          and inspect its version history when information changes.
        docsLabel: Knowledge entries
      - title: Website crawls
        body: Index pages your agents should cite without pasting them into prompts.
        docsLabel: Crawling
      - title: Typed records
        body:
          Contacts and products keep named fields together so people and
          agents can look up specific values alongside supporting documents.
        docsLabel: Structured data
      - title: Access follows the source
        body:
          Team restrictions and project access apply during retrieval. Project
          agents need the corresponding platform tools to reach knowledge.
        docsLabel: Knowledge overview
  faq:
    heading: Knowledge FAQ
    items:
      - q: What is Knowledge in Tale?
        a:
          Knowledge holds shared documents, entries, indexed websites,
          contacts and products. Use it to give questions and project tasks a
          common reference, then verify the sources used.
      - q: What determines which sources can be retrieved?
        a:
          Retrieval follows source permissions and project context. Project
          chats can search their project’s files and accessible organization
          knowledge. Project agents need the relevant tools in their
          equipment.
      - q: What is the difference between documents and typed records?
        a:
          Documents and entries provide text for retrieval. Contacts and
          products store named fields for exact lookups. Check the source and
          its freshness in either case.
      - q: How does a useful fact become shared knowledge?
        a:
          An authorized editor can add or update a knowledge entry;
          integrations can also maintain entries through the API. Verify a
          fact before saving it. Chat does not automatically add facts to the
          library.
  docs:
    - label: Knowledge overview
      path: /platform/knowledge/overview
    - label: Documents
      path: /platform/knowledge/documents
    - label: Knowledge entries
      path: /platform/knowledge/knowledge-entries
    - label: Crawling
      path: /platform/knowledge/crawling
    - label: Structured data
      path: /platform/knowledge/structured-data
  tour:
    heading: How knowledge grounds Tale
    description:
      Prepare company sources, use them in questions and project tasks, and
      inspect the references behind the result.
    stages:
      - id: chat
        eyebrow: Chat
        title: |-
          Open the sources
          behind an answer
        description:
          Retrieved passages land as citation cards so readers can open the
          file or page the answer used.
      - id: projects
        eyebrow: Projects
        title: |-
          Libraries live
          with their teams
        description: Projects scope which documents sit next to which agents and members.
  demos:
    knowledge:
      label:
        'Animated demo: the org library indexes the product manual, the docs site
        crawl, the 2026 price list, and an onboarding FAQ entry.'
      source1: product-manual-v4.pdf
      sourceType1: pdf
      source2: docs.nordwind.eu
      sourceType2: website
      source3: Price list 2026
      sourceType3: catalog
      source4: Onboarding FAQ
      sourceType4: entry
    hero:
      label:
        "Illustrative demo: a chat answers a product question using retrieved
        passages and displays citations to a manual and an FAQ entry."
      prompt: What changed in manual v4 for winter battery care?
      routedTitle: Model selected
      routedDetail: Claude Sonnet answers with retrieved product references.
      reply1: V4 narrows long-term storage to 10–20 °C when parked over 30 days.
      reply2: Charge to 60% before storing — a full charge ages the cells faster.
      reply3: The de-icing warning moved to the quick-start insert,
      reply4: and firmware 3.2 adds a low-temperature charging lock.
      citation1: product-manual-v4.pdf
      citation2: Onboarding FAQ
      composerModel: Claude Sonnet
    projects:
      label: 'Animated demo: Projects that keep libraries next to the agents and
        members who use them.'
      project1: Product corpus
      agents1: 3 agents
      members1: '7'
      project2: Support macros
      agents2: 2 agents
      members2: '9'
      project3: Policy shelf
      agents3: 2 agents
      members3: '5'
      project4: Engineering wiki
      agents4: 4 agents
      members4: '12'
platformProjects:
  eyebrow: Projects
  title: What are Projects in Tale?
  description: Bring teammates, tasks, project agents and reference files together around
    a shared goal. Use the board to plan work, start agent tasks and review
    the results with your team.
  capabilities:
    heading: One workspace per piece of work
    description:
      Coordinate a launch, a research project or an internal improvement with
      a shared board, project instructions and agents equipped for the work.
    items:
      - title: Context that follows the work
        body:
          Keep tasks, instructions and reference files in one project. Project
          chats use its instructions and can retrieve relevant files.
        docsLabel: Projects overview
      - title: A board agents work from
        body:
          Assign a task to a project agent, then start it. Follow progress and
          review the report and attached files when the agent finishes.
        docsLabel: Task automation
      - title: A backlog for proposed work
        body:
          Collect ideas and agent-proposed tasks in the backlog. Clarify the
          brief, choose an assignee and decide which tasks to start.
        docsLabel: Project backlog
      - title: Files scoped to the project
        body:
          Organize reference files in the project’s Knowledge tab. Indexed
          content is available in that project’s context, subject to access
          rules.
        docsLabel: Manage files
      - title: Agents equipped for your project
        body: Create project agents with a role, compatible runtime and model,
          instructions and tools. Assign work to the agent prepared for that
          task.
        docsLabel: Project agents
  faq:
    heading: Projects FAQ
    items:
      - q: What is a project in Tale?
        a: A shared workspace that bundles chats, files, instructions, and tasks
          around one piece of work — a contact, a launch, an investigation — so
          the context stays with the work.
      - q: How do agents work on project tasks?
        a:
          Assign a task, then start the agent or move the task to In progress.
          The agent works in its sandbox and returns a report and files for
          review. A person or a separately assigned reviewer agent can review
          the result; assignment alone does not start a run.
      - q: Who sees a project's chats and files?
        a:
          Projects are organization-wide by default. Restrict the project
          audience to selected teams when needed; administrators retain
          access. Chats remain personal until explicitly shared, including
          chats created inside a project.
      - q: How is a project different from a chat?
        a:
          A chat is a conversation with an AI assistant. A project organizes
          ongoing work across a task board, agents, instructions, files and
          chats. Starting a new chat uses the project instructions, but does
          not automatically import earlier conversations.
  docs:
    - label: Projects overview
      path: /platform/projects/overview
    - label: Project concepts
      path: /platform/projects/concepts
    - label: Task automation
      path: /platform/projects/task-automation
    - label: Project backlog
      path: /platform/projects/backlog
    - label: Manage files
      path: /platform/projects/manage-files
    - label: Project agents
      path: /platform/projects/project-agents
  tour:
    heading: How projects run work
    description: Plan work with your team, delegate tasks to agents and review their
      reports. Use project instructions and reference files to support the
      work.
    stages:
      - id: tasks
        eyebrow: Tasks
        title: |-
          Assign a task,
          then start the agent
        description:
          Give the task a clear brief and assign a project agent. Start the
          work, follow its progress and inspect the report and files it
          returns for review.
      - id: chat
        eyebrow: Chats
        title: |-
          Give project chats
          a common brief
        description:
          Project instructions apply to its chats, and the assistant can
          retrieve relevant project files. Add any earlier decisions that a
          new conversation needs.
  demos:
    hero:
      label:
        "Illustrative demo: a project chat uses project instructions and
        retrieved reference files to answer a question about a website
        relaunch."
      prompt: What's left before the relaunch goes live?
      routedTitle: Model selected
      routedDetail: The chat assistant uses project instructions and retrieves relevant
        files.
      reply1:
        The launch checklist still lists the redirect-map review and the
        pricing page copy.
      reply2: It calls for a staging pass once both are ready.
      reply3: DNS cutover is booked with IT for Thursday 06:00,
      reply4: and the checklist flags the untested /docs redirect as an open risk.
      citation1: launch-checklist.pdf
      citation2: Redirect map
      composerModel: Claude Opus
    govern:
      label: "Illustrative demo: a configured publishing workflow pauses for
        connector approval before attempting to publish the new pricing page."
      approvalTitle: Publish the new pricing page to production?
      requester: Website relaunch · Publishing workflow
      audit1: Approved by mira@nordwind.eu
      audit2: Pricing page published
      audit3: Decision recorded in the audit log
      budgetValue: CHF 120 / 400
    tasks:
      label:
        "Animated demo: the Website relaunch board. Relaunch Agent works on WEB-12 in In progress while WEB-11 waits in In review."
      id1: WEB-14
      title1: Finish /docs redirect map
      assignee1: Unassigned
      id2: WEB-15
      title2: Rewrite pricing hero copy
      assignee2: Mira
      id3: WEB-12
      title3: Draft migration guide for relaunch
      assignee3: Relaunch Agent
      id4: WEB-11
      title4: Sign off staging checklist
      assignee4: Relaunch Agent
      id5: WEB-9
      title5: Publish brand token set
      assignee5: Design Agent
platformGovernance:
  eyebrow: Governance
  title: Set the rules for your team and agents
  description: Control access, configure connector approvals, and review recorded
    actions. Set model usage budgets and chat filters for the work your team
    and agents carry out.
  capabilities:
    heading: What does governance cover?
    description: Choose which connector writes need approval, which tools agents may use,
      and which content and usage rules apply.
    items:
      - title: Approve connector writes
        body:
          Automation connector writes pause when your policy requires
          approval. External writes require it by default; reads and internal
          platform writes follow different rules.
        docsLabel: Approval concepts
      - title: Inspect the proposed operation
        body:
          Review the destination and exact input before approving. Reject an
          incorrect operation, fix the workflow or input, and start a new run.
        docsLabel: Approval concepts
      - title: Read the audit trail
        body:
          Inspect recorded actions and approval decisions with their actor and
          time. Use the audit log to investigate what happened.
        docsLabel: Audit logs
      - title: Set organization rules
        body:
          Configure shared instructions, access policies, and agent tool
          grants. Enforced permissions and filters complement the instructions
          you give a model.
        docsLabel: Guardrails
      - title: Configure chat content filters
        body:
          Enable content safety, personal-data detection, and optional
          moderation for the chat directions you choose. Test the patterns and
          failure behavior your work needs.
        docsLabel: Guardrails
      - title: Set budgets for metered work
        body: Track recorded model usage and set budget rules for supported paths,
          including managed agent runs. Direct subscription calls use the
          provider’s own controls.
        docsLabel: Usage analytics
  faq:
    heading: Governance FAQ
    items:
      - q: What does governance control?
        a:
          Governance combines access rules, connector approval policies,
          recorded audit events, usage budgets, and configurable chat filters.
          Each control has its own scope.
      - q: Where do I decide a connector approval?
        a:
          Open the waiting automation run. If it works on a task, the approval
          card also appears on that task. Connector approvals are distinct
          from reviewing an agent’s completed task.
      - q: Can I choose which connector writes require approval?
        a: Yes. The policy can be configured for a connector or a specific
          action. External writes require approval by default; changing the
          policy does not resolve an approval already waiting.
      - q: Does every agent action wait for approval?
        a:
          No. The connector broker exposes read actions to agents; governed
          connector writes run through automation nodes. Direct GitHub tooling
          and explicitly granted secrets have separate access paths. Review
          those grants too.
      - q: How do content filters and data controls apply?
        a: Chat filters can check configured input and output. Model API
          endpoints filter input only; they do not filter model output.
          Personal-data detection depends on configured patterns. Assess
          deployment, providers, and operating procedures together.
  docs:
    - label: Approval concepts
      path: /platform/approvals/concepts
    - label: Configure approvals
      path: /platform/approvals/configure
    - label: Approvals in workflows
      path: /platform/automations/approvals-in-workflows
    - label: Audit logs
      path: /platform/admin/governance/audit-logs
    - label: Guardrails
      path: /platform/admin/governance/guardrails
  tour:
    heading: See the rules in the work
    description: Inspect workflow runs, compare answers, and check the tools and sources
      available to agents.
    stages:
      - id: automations
        eyebrow: Automations
        title: |-
          Follow the run
          and its decisions
        description:
          A configured workflow can review an access report and prepare a
          notification. Inspect its steps, approval decisions, and recorded
          effects in the run detail.
      - id: agents
        eyebrow: Agents
        title: |-
          Choose the tools
          the work needs
        description:
          Equip each project agent with explicit tools and credentials.
          Managed model calls use Tale’s gateway controls; direct subscription
          access follows a different path.
  demos:
    govern:
      label:
        "Illustrative demo: an automation asks approval before a connector
        sends a policy update email, then records the decision and execution."
      approvalTitle: Send the policy update to the project team?
      requester: Policy workflow · Mail connector
      audit1: Approved by lena@nordwind.eu
      audit2: Policy update email sent
      audit3: 'Audit entry #8841 written'
      budgetValue: CHF 220 / 800
    automation:
      label:
        "Illustrative demo: a configured scheduled workflow reviews an access
        report and prepares a Slack notification."
      trigger: Daily 01:00
      llm: Review access
      condition: New admins?
      branchYes: Yes
      action: 'Alert #security'
      actionAlt: Archive report
      run1: '#98'
      runTone1: done
      duration1: 3.4s
      run2: '#99'
      runTone2: done
      duration2: 3.1s
      run3: '#100'
      runTone3: running
      duration3: 0.6s
    connect:
      label:
        "Illustrative demo: project agents with distinct responsibilities and
        selected models; their tools and permissions are configured
        separately."
      agent1: Policy Agent
      model1: Claude Sonnet
      agent2: Audit Agent
      model2: Claude Opus
      agent3: Budget Agent
      model3: GPT-5.5
      agent4: Access Agent
      model4: Claude Sonnet
      agent5: Exceptions Agent
      model5: Claude Opus
pricingPage:
  relatedHeading: Related
  ctaTitle: Put a number on your setup
  ctaDescription: Request a demo or talk to the team about Enterprise seats,
    storage, and hardware.
  faq:
    heading: Pricing FAQ
    items:
      - q: How much does Tale Enterprise cost?
        a:
          Tale Enterprise is CHF 12 (EUR 14) per user/month, with two months free on
          yearly billing. Storage is CHF 10 (EUR 12) per TB/month.
      - q: Is Community free?
        a: Yes. The open-source Community edition is free to self-host.
      - q: Can I buy hardware with software?
        a:
          Yes. Silent inference hardware is available to rent, lease, or buy — see
          Hardware pricing.
changelogPage:
  eyebrow: Changelog
  title: What's new in Tale?
  description: Release notes for Tale, newest first — published from GitHub Releases.
  allReleases: All releases
  sourceNote: Sourced from
  githubLink: GitHub Releases
  viewOnGithub: View on GitHub
  emptyBody: No release notes for this version.
  fetchedAt: Last updated {date}.
contentPages:
  comparisons:
    directoryTitle: Find your comparison
    directoryDescription: Start with a product you know, or narrow the guides by the kind of decision you need to make.
    searchLabel: Search comparisons
    searchPlaceholder: Product name or topic
    filterLabel: Comparison type
    resultCount: '{count} of {total} guides'
    emptyTitle: No matching comparisons
    emptyDescription: Try another product name or topic, or clear your search and filters to see every guide.
    reset: Show all comparisons
    sameBrief: One brief, two approaches
    workspace: Shared project workspace
    criteriaLabel: What this guide compares
    types:
      all:
        label: All guides
        description: Compare the workflow your team needs, including where different products can work together.
      direct:
        label: Team platforms
        description: Explore products for coordinating people and agents, delegating tasks, and reviewing shared work.
      adjacent:
        label: Assistants & tools
        description: Compare assistants, workflow tools, and app builders with a shared project workspace.
      framework:
        label: Frameworks
        description: Decide whether to adopt a workspace or build your own agent application and team interface.
      runtime:
        label: Agent runtimes
        description: Understand the difference between a compatible agent runtime and the workspace around it.
  tableScrollHint: Scroll sideways to compare
  breadcrumb: Page path
  reviewed: 'Reviewed {date}'
  relatedTitle: Work you can try with your team
  relatedDescription: Start with a clear brief, defined deliverables, and a review process.
  nextTitle: Try it with your own project
  nextDescription: Bring a real task and the tools your team already uses.
  allUseCases: All use cases

demo:
  pages:
    home:
      label: "Tale task workspace for reviewing a launch brief and discussing the next step."
      caption: "The task, its context and the next decision in one place."
    hub:
      label: "Tale inbox for reading customer conversations and drafting a reply."
      caption: "Projects, chats and customer conversations in one workspace."
    agents:
      label: "Tale project Agents tab for inspecting an agent’s configuration."
      caption: "Each project agent has its own runtime, model, instructions and equipment."
    chat:
      label: "Tale chat workspace for writing prompts and reading AI responses."
      caption: "Write a prompt, follow the reply and continue the work."
    projects:
      label: "Tale project task board showing tasks, their status and assignee details."
      caption: "See the whole project, from proposed work to review and completion."
    automations:
      label: "Tale automation run showing a completed workflow, node outcomes, its version and run status."
      caption: "Inspect the workflow and the outcome of each step in a real run."
    knowledge:
      label: "Tale Knowledge library showing shared onboarding, brand and support entries."
      caption: "Keep onboarding, brand and support material in a shared library."
    governance:
      label: "Tale Governance audit logs showing recorded actions and who performed them."
      caption: "Review who changed what, inspect log integrity and export the audit trail."
